import { DCMConstants } from "../DCM.constants";

export class SharedFunctions {

    // Updates a url based on the prefix for the region and the country code
    public static updateUrlRegionPrefix(url: string, countryCode: string): string {
        let urlPrefix = '';
        const countryCodeFormatted = countryCode.trim().toUpperCase();

        urlPrefix = DCMConstants.REGIONS.AMERICA.COUNTRIES.includes(countryCodeFormatted) ? DCMConstants.REGIONS.AMERICA.PREFIX : 
                    DCMConstants.REGIONS.EUROPE.COUNTRIES.includes(countryCodeFormatted) ? DCMConstants.REGIONS.EUROPE.PREFIX : '';
    
        return urlPrefix + url;            
    }
    
    public filterData(items: any[], filter: { [x: string]: string; jobsiteCode?: string; jobsiteDesc?: string; customerCode?: string; customerDesc?: string; vat?: string; fullName?: string; userAccount?: string; cityDesc?: string; requesterName?: string; }) {
        if (filter && Array.isArray(items)) {
            const filterKeys = Object.keys(filter);
            return items.filter((item) => {
                return filterKeys.some((keyName) => {
                    const value = filter[keyName].replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                    return new RegExp(value, 'gi').test(item[keyName]) || value === '';
                });
            });
        } else {
            return items;
        }
    }

    public getUserLanguageByCountry(countryCode: string):string {
        const countryName: string = '';
        if (countryCode === null || countryCode === '' || countryCode === undefined) { return countryName; }
        const countryCodeFormatted = countryCode.trim().toUpperCase();
        return countryCodeFormatted === 'MX' ? 'views.header.spanish' : 'views.header.english';
    }

    public getCountryName(countryCode: string): string {
        let countryName: string = '';
        if (countryCode === null || countryCode === '' || countryCode === undefined) { return countryName; }

        const countryCodeFormatted = countryCode.trim().toUpperCase();

        if (countryCodeFormatted === 'MX') {
            countryName = 'views.global.mxLabel';
        } else {
            countryName = 'views.global.usLabel';
        }

        return countryName;
    }

    public getStatusName(statusCode: string): string {
        if (statusCode === 'N' || statusCode === 'New') {
            return 'views.global.statusNew';
        } else if (statusCode === 'A' || statusCode === 'Approved') {
            return 'views.global.statusApproved';
        } else if (statusCode === 'R' || statusCode === 'Rejected') {
            return 'views.global.statusRejected';
        } else if (statusCode === 'P' || statusCode === 'Pending') {
            return 'views.global.statusPending';
        } else if (statusCode === 'C' || statusCode === 'Cancelled') {
            return 'views.global.statusCancelled';
        } else {
            return '';
        }
    }

    public getRequestTypeName(requestType: string): string {
      if (requestType === 'R' || requestType === 'Role') {
        return 'views.requestDetailModal.lblRole';
      } else if (requestType === 'D' || requestType === 'Data') {
        return 'views.requestDetailModal.lblData';
      } else if (requestType === 'S') {
        return 'views.requestDetailModal.lblNewAccess';
      } else { return ''; }
    }

    public formatRolesString(rolesList: string): string {
        let formattedRolesList: string = '';
        const roles: string[] = rolesList.split(',');
        if (roles.length > 0) {
            roles.forEach((role) => {
                if (role) {
                    formattedRolesList += role + ', ';
                }
            });
        }

        if (formattedRolesList.slice(-1) === ' ') {
            formattedRolesList = formattedRolesList.substr(0, formattedRolesList.length - 2);
        }

        return formattedRolesList;
    }

    /**
     * Encodes a JSON-serializable object into Base64 and stores it in localStorage or sessionStorage.
     *
     * @template T - The type of the input data to be stored.
     * @param storage - Storage type or object ('local', 'session', or a Storage instance).
     * @param key - The key under which the data should be stored.
     * @param value - The object to encode and store.
     */
    public storeBase64StorageJSON(storage: 'local' | 'session' | Storage, key: string, value: unknown): void {
        const encoded = this.safeFn(
            () => {
                const json = JSON.stringify(value);
                const bytes = new TextEncoder().encode(json);
                const binary = String.fromCharCode(...bytes);
                return btoa(binary);
            },
            `[CMX] Error encoding base64 data`
        );

        if (encoded !== null) {
            const dataStorage = this.resolveStorage(storage);
            dataStorage.setItem(key, encoded);
        } else {
            console.error(`[CMX] Error encoding base64 for ${this.getStorageName(storage)} key: "${key}"`);
        }
    }

    /**
     * Parses a Base64-encoded JSON object from localStorage or sessionStorage.
     *
     * @template T - The expected type of the parsed object.
     * @param storage - Storage type or object ('local', 'session', or a Storage instance).
     * @param key - The key under which the Base64-encoded data is stored.
     * @returns Parsed object of type T, or null if data is missing or invalid.
     */
    public parseBase64StorageJSON<T>(storage: 'local' | 'session' | Storage, key: string): T | null {
        const data = this.getDataFromStorage(storage, key);

        if (!data) {
            return null;
        }

        const decoded = this.safeFn(
            () => new TextDecoder().decode(Uint8Array.from(atob(data), c => c.charCodeAt(0))),
            `[CMX] Error decoding base64 data from ${this.getStorageName(storage)} key: "${key}"`,
        );

        return this.safeParse<T>(decoded);
    }

    /**
     * Parses a plain JSON object (not Base64-encoded) from localStorage or sessionStorage.
     *
     * @template T - The expected type of the parsed object.
     * @param storage - Storage type or object ('local', 'session', or a Storage instance).
     * @param key - The key under which the JSON string is stored.
     * @returns Parsed object of type T, or null if data is missing or invalid.
     */
    public parseFromStorage<T>(storage: 'local' | 'session' | Storage, key: string): T | null {
        const data = this.getDataFromStorage(storage, key);

        if (!data) {
            return null;
        }

        const errMessage = `[CMX] Error parsing data from ${this.getStorageName(storage)} key: "${key}"`;
        return this.safeParse<T>(data, errMessage);
    }

    /**
     * Safely parses a JSON string into an object, with optional error logging.
     *
     * @template T - The expected type of the parsed object.
     * @param data - JSON string to parse.
     * @param errMessage - Optional custom error message to log if parsing fails.
     * @returns Parsed object of type T, or null if parsing fails.
     */
    public safeParse<T>(data?: string, errMessage?: string): T | null {
        if (!data) {
            return null;
        }

        return this.safeFn(
            () => JSON.parse(data),
            errMessage ?? '[CMX] Error parsing data:'
        );
    }

    /**
     * Executes a function safely, returning its result or null if an error occurs.
     *
     * @template T - The return type of the executed function.
     * @param fn - The function to execute.
     * @param errMessage - Optional custom error message to log if execution fails.
     * @returns The result of the function if successful, or null if an error is thrown.
     */
    public safeFn<T>(fn: () => T, errMessage?: string): T | null {
        try {
            return fn();
        } catch (error) {
            const defaultMessage = '[CMX] Error executing function:';
            console.warn(errMessage ?? defaultMessage, error);
            return null;
        }
    }

    private getDataFromStorage(storage: 'local' | 'session' | Storage, key: string): string | null {
        const dataStorage = this.resolveStorage(storage);
        return dataStorage.getItem(key);
    }

    private getStorageName(storage: 'local' | 'session' | Storage): 'LocalStorage' | 'SessionStorage' {
        const isLocal = storage === 'local' || storage === localStorage;
        return isLocal ? 'LocalStorage' : 'SessionStorage';
    }

    private resolveStorage(storage: 'local' | 'session' | Storage): Storage {
        const isLocal = storage === 'local' || storage === localStorage;
        return isLocal ? localStorage : sessionStorage;
    }
}
